/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Operations Service
 *
 * Service for fetching available connectors and operations from Azure APIs.
 * Based on LogicAppsUX StandardSearchService patterns.
 */

import type { ConnectorManifest } from '@/types/connections';
import type { OperationDefinition, OperationCategory } from '@/types/operations';
import type { SwaggerSpec } from '@/types/swagger';
import {
  isLocalMode,
  localFetch,
  LocalFetchOptions,
} from './localRuntimeClient';

/**
 * Discovery operation from the Azure API
 * Matches LogicAppsUX DiscoveryOperation format
 */
export interface DiscoveryOperation {
  id: string;
  name: string;
  type: string;
  kind?: string;
  /**
   * Optional pre-seeded action `inputs` shape. Not part of the upstream
   * discovery payload; attached client-side when synthesizing variants of a
   * single operation (e.g. Coding vs Foundry vs Native agent) so the
   * downstream node-creation path lands the right defaults.
   */
  defaultInputs?: unknown;
  properties: {
    summary?: string;
    description?: string;
    visibility?: string;
    operationType?: string;
    brandColor?: number | string;
    iconUri?: string;
    api: {
      id: string;
      name?: string;
      connectorId?: string;
      displayName?: string;
      description?: string;
      iconUri?: string;
      brandColor?: string;
    };
    trigger?: string; // Present if this is a trigger operation
    capabilities?: string[];
    /** True if this is a webhook-based trigger (uses ApiConnectionWebhook type) */
    isWebhook?: boolean;
    /** True if this is a notification-based trigger (uses ApiConnectionNotification type) */
    isNotification?: boolean;
    /** True if this operation's connector is a custom-built API rather than a managed one. */
    isCustomApi?: boolean;
  };
}

/**
 * Connector from the OperationGroups API
 */
export interface Connector {
  id: string;
  name: string;
  type?: string;
  kind?: string;
  properties: {
    displayName: string;
    description?: string;
    iconUri: string;
    brandColor: number | string;
    capabilities?: string[];
    generalInformation?: {
      displayName?: string;
      description?: string;
      iconUrl?: string;
    };
    metadata?: {
      brandColor?: string;
    };
  };
}

export type FetchOptions = {
  projectName: string;
  appName: string;
  accessToken: string;
};

export type AppFetchOptions = {
  projectName: string;
  accessToken: string;
}

import { isArmMockMode } from './armConfig';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { isManagedApiOperation, normalizeConnectorId, isServiceProviderConnector } from '@/utils/connections';
import { getAppUrl } from './appClient';
import { AppResourceContext, ConnectorMetadata } from './connectionApi';

/**
 * Fetch wrapper that supports both local and cloud modes.
 * In local mode, skips auth token and uses local fetch.
 */
const fetchWithAuth = async <T>(url: string, accessToken: string, method = 'GET'): Promise<T> => {
  if (isLocalMode() || isArmMockMode()) {
    return localFetch<T>(url, { method } as LocalFetchOptions);
  }

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API request failed: ${response.status} ${errorText}`);
  }

  return response.json();
};

/**
 * Converts a hex number or string to a CSS color string
 */
const hexToColor = (hex: number | string | undefined): string => {
  if (!hex) return '#0078D4';
  if (typeof hex === 'string') {
    return hex.startsWith('#') ? hex : `#${hex}`;
  }
  return `#${hex.toString(16).padStart(6, '0')}`;
};

/**
 * Fetches all built-in operations from the Logic App runtime
 * GET /runtime/webhooks/workflow/api/management/operations
 */
export const fetchBuiltInOperations = async (
  opts: FetchOptions,
  workflowKind: 'Stateful' | 'Stateless' = 'Stateful'
): Promise<DiscoveryOperation[]> => {
  const baseUrl = `${getAppUrl(opts)}/operations?api-version=${apiVersions.otto}&workflowKind=${workflowKind}`;
  const allOps: DiscoveryOperation[] = [];

  try {
    let nextUrl: string | undefined = baseUrl;
    do {
      const response: { value: DiscoveryOperation[]; nextLink?: string } = await fetchWithAuth<{ value: DiscoveryOperation[]; nextLink?: string }>(nextUrl, opts.accessToken);
      allOps.push(...(response.value ?? []));
      nextUrl = response.nextLink;
    } while (nextUrl);
    return allOps.filter(filterUnsupportedOperation);
  } catch (error) {
    console.warn('Failed to fetch built-in operations:', error);
    return [];
  }
};

/**
 * Fetches all built-in connectors (operation groups) from the Logic App runtime
 * GET /runtime/webhooks/workflow/api/management/operationGroups
 */
export const fetchBuiltInConnectors = async (opts: FetchOptions): Promise<Connector[]> => {
  const baseUrl = `${getAppUrl(opts as any)}/operationGroups?api-version=${apiVersions.otto}`;
  const allConnectors: Connector[] = [];

  try {
    let nextUrl: string | undefined = baseUrl;
    do {
      const response: { value: Connector[]; nextLink?: string } = await fetchWithAuth<{ value: Connector[]; nextLink?: string }>(nextUrl, opts.accessToken);
      allConnectors.push(...(response.value ?? []));
      nextUrl = response.nextLink;
    } while (nextUrl);
    return allConnectors.filter(filterUnsupportedConnector);
  } catch (error) {
    console.warn('Failed to fetch built-in connectors:', error);
    return [];
  }
};

/**
 * Fetches a specific built-in connector (operation group) from the Logic App runtime
 * GET /runtime/webhooks/workflow/api/management/operationGroups/{connectorName}
 */
export const fetchBuiltInConnectorMetadata = async (ctx: AppResourceContext, connectorName: string): Promise<ConnectorMetadata> => {
  const baseUrl = `${getLogicEndpoint()}${ctx.appId}/operationGroups/${encodeURIComponent(connectorName)}?api-version=${apiVersions.otto}`;

  try {
    const response: {
    id: string;
    name: string;
    properties: {
      displayName: string;
      iconUri?: string;
      description?: string;
      brandColor?: string;
      connectionParameters?: Record<string, unknown>;
      connectionParameterSets?: {
        values: Array<{
          name: string;
          uiDefinition: {
            displayName: string;
            description?: string;
          };
          parameters: Record<string, unknown>;
        }>;
      };
      connectionAlternativeParameters?: Record<string, unknown>;
    };
  } = await fetchWithAuth(baseUrl, ctx.accessToken);

    const result: ConnectorMetadata = {
      id: response.id,
      name: response.name,
      displayName: response.properties.displayName ?? response.name,
    };

    if (response.properties.iconUri) {
      result.iconUri = response.properties.iconUri;
    }
    if (response.properties.brandColor) {
      result.brandColor = response.properties.brandColor;
    }
    if (response.properties.connectionParameters) {
      result.connectionParameters = response.properties.connectionParameters;
    }
    if (response.properties.connectionParameterSets?.values) {
      result.connectionParameterSets = response.properties.connectionParameterSets.values;
    }
    if (response.properties.connectionAlternativeParameters) {
      result.connectionAlternativeParameters = response.properties.connectionAlternativeParameters;
    }

    return result;
  } catch (error) {
    console.warn(`Failed to fetch built-in connector ${connectorName}:`, error);
    throw error;
  }
};

/**
 * Fetches Azure managed connectors (API Hub connectors)
 * Uses the Azure API Hub endpoint
 */
export const fetchAllAzureConnectors = async (
  opts: AppFetchOptions,
): Promise<Connector[]> => {
  let next: string | undefined;
  const connectors: Connector[] = [];
  const project = encodeURIComponent(opts.projectName);

  do {
    const { value, nextLink } = await fetchWithAuth<{ value: Connector[]; nextLink?: string }>(
      next ??
        `${getLogicEndpoint()}/automationProjects/${project}/managedApis?api-version=${apiVersions.otto}&$top=1000`,
      opts.accessToken
    );

    connectors.push(...(value ?? []));
    next = nextLink;
  } while (next);

  return connectors.map(toConnector);
};

/**
 * Page size used for the cross-connector `/apiOperations` paginated fetch.
 * The Otto control plane accepts `$top` up to 5000, so we request the
 * maximum on every page. A typical project has ~40k operations across all
 * managed APIs; at 5000/page that's ~8 round trips, which dramatically
 * shortens the time-to-full-corpus (and therefore the time-to-cache-write)
 * on first load.
 */
export const AZURE_OPERATIONS_PAGE_SIZE = 5000;

export interface PagedOperationsResult {
  /** The operations returned by this page. */
  data: DiscoveryOperation[];
  /** Opaque next-page URL; absent when there are no more pages. */
  nextLink?: string;
}

/**
 * Fetches one page of Azure managed-API operations across every connector
 * in the project, driven by `useInfiniteQuery`. Pass `undefined` for the
 * first call and the previous result's `nextLink` thereafter; iteration
 * ends when `nextLink` is absent.
 *
 * Errors are intentionally not caught: swallowing them would produce an
 * empty page with no `nextLink`, which `useInfiniteQuery` would treat as
 * a clean end-of-pagination and let a partial corpus reach the 24h IDB
 * cache.
 */
export const fetchAzureOperationsByPage = async (
  opts: AppFetchOptions,
  pageParam?: string,
): Promise<PagedOperationsResult> => {
  const url =
    pageParam ??
    `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/apiOperations?$top=${AZURE_OPERATIONS_PAGE_SIZE}&api-version=${apiVersions.otto}`;

  const response = await fetchWithAuth<{ value: DiscoveryOperation[]; nextLink?: string }>(
    url,
    opts.accessToken,
  );
  const data = (response.value ?? []).filter(filterUnsupportedOperation);
  const result: PagedOperationsResult = { data };
  if (response.nextLink) {
    result.nextLink = response.nextLink;
  }

  return result;
};

/**
 * Fetches operations for a specific built-in connector
 */
export const fetchOperationsForConnector = async (
  opts: FetchOptions,
  connectorId: string
): Promise<DiscoveryOperation[]> => {
  const encodedId = encodeURIComponent(connectorId);
  const url = `${getAppUrl(opts as any)}/operationGroups/${encodedId}/operations?api-version=${apiVersions.otto}`;

  try {
    const response = await fetchWithAuth<{ value: DiscoveryOperation[] }>(url, opts.accessToken);
    return response.value;
  } catch (error) {
    console.warn(`Failed to fetch operations for connector ${connectorId}:`, error);
    return [];
  }
};

/**
 * Fetches operations for a specific Azure managed connector. `source`
 * tags the call site (picker drill-in vs assistant catalog) so the
 * network tab can distinguish otherwise-identical requests.
 */
export const fetchOperationsForAzureConnector = async (
  opts: AppFetchOptions,
  connectorName: string,
  actionType?: 'triggers' | 'actions',
  source: 'picker-drillin' | 'assistant-catalog' | 'unknown' = 'unknown',
): Promise<DiscoveryOperation[]> => {

  let url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/managedApis/${encodeURIComponent(connectorName)}/apiOperations?$top=200&api-version=${apiVersions.otto}`;

  if (actionType !== undefined) {
    url += `&$filter=${actionType === 'triggers' ? 'properties/trigger ne null' : 'properties/trigger eq null'}`;
  }

  try {
    const response = await fetchWithAuth<{ value: DiscoveryOperation[] }>(url, opts.accessToken);
    return response.value || [];
  } catch (error) {
    console.warn(`Failed to fetch operations for Azure connector ${connectorName} (source=${source}):`, error);
    return [];
  }
};

/**
 * Fetches the full swagger spec for an Azure managed connector
 * Uses export=true to get parameter definitions
 */
export const fetchConnectorSwagger = async (
  opts: AppFetchOptions,
  connectorId: string
): Promise<SwaggerSpec | null> => {
  const normalizedId = normalizeConnectorId(connectorId);
  const connectorName = normalizedId?.split('/').slice(-1)[0] ?? ''; // Extract connector name from normalized ID
  // Fetch swagger with export=true to get full spec including parameters
  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/managedApis/${encodeURIComponent(connectorName)}/listSwagger?api-version=${apiVersions.otto}`;

  try {
    // The API may return swagger directly at root level or nested under properties.swagger
    const response = await fetchWithAuth<SwaggerSpec | { properties?: { swagger?: SwaggerSpec } }>(url, opts.accessToken, 'POST');

    // Check if swagger is at root level (has 'swagger' or 'openapi' field) or nested
    if ('swagger' in response || 'openapi' in response) {
      return response as SwaggerSpec;
    } else if ('properties' in response && response.properties?.swagger) {
      return response.properties.swagger;
    }

    return null;
  } catch (error) {
    console.warn(`Failed to fetch swagger for connector ${connectorName}:`, error);
    return null;
  }
};

/**
 * Performs fuzzy search for operations across Azure managed APIs
 * Uses Azure's backend search with filters
 */
export const fetchFuzzySearchOperations = async (
  opts: AppFetchOptions,
  searchTerm: string,
  actionType?: 'trigger' | 'action'
): Promise<DiscoveryOperation[]> => {
  if (!searchTerm.trim()) {
    return [];
  }

  // Build filter conditions
  const filters = [];

  // Add action type filter
  if (actionType === 'trigger') {
    filters.push('properties/trigger ne null');
  } else if (actionType === 'action') {
    filters.push('properties/trigger eq null');
  }

  // Build query parameters
  const queryParams = new URLSearchParams({
    '$filter': filters.join(' and '),
    '$search': `"${searchTerm}"`,
    'api-version': apiVersions.otto,
  });

  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/apiOperations?${queryParams}`;

  try {
    const response = await fetchWithAuth<{ value: DiscoveryOperation[] }>(url, opts.accessToken);
    return response.value || [];
  } catch (error) {
    console.warn(`Failed to perform fuzzy search for "${searchTerm}":`, error);
    return [];
  }
};

/**
 * Check if an operation is a Service Provider operation
 * Service Provider operations have:
 * - operationType === 'ServiceProvider', OR
 * - api.id containing '/serviceProviders/'
 */
export function isServiceProviderOperation(op: DiscoveryOperation): boolean {
  const operationType = op.properties.operationType?.toLowerCase();
  if (operationType === 'serviceprovider') return true;

  const apiId = op.properties.api?.id;
  if (apiId && isServiceProviderConnector(apiId)) return true;

  return false;
}

/**
 * Convert a DiscoveryOperation to OperationDefinition
 */
export const discoveryOperationToDefinition = (op: DiscoveryOperation): OperationDefinition => {
  const isTrigger = !!op.properties.trigger;
  const brandColor = hexToColor(op.properties.brandColor || op.properties.api?.brandColor);

  // Azure managed API operations have type "automationProjects/managedApis/apiOperations"
  // but should be created as "ApiConnection" type in the workflow
  const isManagedOperation = isManagedApiOperation(op);

  // Service Provider operations should have type "ServiceProvider" in the workflow
  // They come from runtime API with operationType: 'ServiceProvider' or api.id containing '/serviceProviders/'
  const isServiceProvider = isServiceProviderOperation(op);

  // Determine the action type for the workflow
  // Logic follows LogicAppsUX pattern from add.ts getOperationType():
  // 1. Use explicit operationType if provided
  // 2. For managed API operations, check isWebhook/isNotification flags
  // 3. Default to ApiConnection for managed APIs
  let actionType: string;
  if (op.properties.operationType) {
    // Explicit operationType takes precedence
    actionType = op.properties.operationType;
  } else if (isManagedOperation) {
    // For Azure managed APIs, check trigger type flags
    // isWebhook: Uses ApiConnectionWebhook (callback-based, e.g., HTTP webhook)
    // isNotification: Uses ApiConnectionNotification (subscription-based, e.g., "When a new email arrives")
    if (op.properties.isWebhook) {
      actionType = 'ApiConnectionWebhook';
    } else if (op.properties.isNotification) {
      actionType = 'ApiConnectionNotification';
    } else {
      actionType = 'ApiConnection';
    }
  } else if (isServiceProvider) {
    actionType = 'ServiceProvider';
  } else {
    // Fall back to op.type for other operations
    actionType = op.type;
  }

  // Azure managed API operations are "connector" operations (require a connection).
  // Built-in and service-provider operations come from the runtime and don't require connections.
  const isConnector = isManagedOperation;

  const result: OperationDefinition = {
    id: op.id,
    name: op.properties.summary || op.name,
    type: actionType,
    category: isTrigger ? 'trigger' : 'action' as OperationCategory,
    brandColor,
    isConnector,
    connectorId: op.properties.api?.id,
  };

  if (op.kind) {
    result.kind = op.kind;
  }
  if (op.properties.description) {
    result.description = op.properties.description;
  }
  const iconUri = op.properties.iconUri || op.properties.api?.iconUri;
  if (iconUri) {
    result.icon = iconUri;
  }
  if (op.properties.api?.displayName) {
    result.connectorName = op.properties.api.displayName;
  }
  if (op.defaultInputs !== undefined) {
    result.defaultInputs = op.defaultInputs;
  }

  return result;
};

/**
 * Convert a Connector to ConnectorManifest
 */
export const connectorToManifest = (connector: Connector): ConnectorManifest => {
  const result: ConnectorManifest = {
    id: connector.id,
    name: connector.name,
    displayName: connector.properties.displayName || connector.properties.generalInformation?.displayName || connector.name,
    iconUri: connector.properties.iconUri || connector.properties.generalInformation?.iconUrl || '',
    brandColor: hexToColor(connector.properties.brandColor),
  };

  const description = connector.properties.description || connector.properties.generalInformation?.description;
  if (description) {
    result.description = description;
  }

  return result;
};

/**
 * Get all client-side built-in operations (hardcoded for offline use / fallback)
 * Returns operations in DiscoveryOperation format for consistency with API results
 */
export { getClientBuiltInOperations } from '@/data/clientOperations';

/**
 * Browse category type
 *
 * - `immediate`: clicking the card adds an operation directly (no filtered list).
 * - `browse`: clicking the card filters the connector list by `connectorFilters.connectorIds`.
 * - `favorites`: dynamic browse category — the picker resolves `connectorIds` from
 *   the user's local favorites store at render time. `connectorFilters` is ignored.
 * - `mcp` / `subworkflows`: agent-tool categories — the picker renders a dedicated
 *   browser (MCP server tree or callable-workflow list) instead of the connector list.
 *   Only surfaced when the picker is opened in `target='agent-tools'` mode.
 */
export type BrowseCategoryType = 'immediate' | 'browse' | 'favorites' | 'mcp' | 'subworkflows';

/**
 * Category definitions for operation browsing.
 */
export interface BrowseCategory {
  key: string;
  title: string;
  description: string;
  icon: string; // Fluent icon name
  type: BrowseCategoryType;
  visible?: boolean;
  connectorFilters?: {
    connectorIds?: string[];
  };
  /** For immediate categories, the operation to add directly */
  operationId?: string;
}

// Connector ID groups shared across trigger/action category definitions.
const COMMUNICATION_CONNECTORS = [
  // Email
  'office365',
  'outlook',
  'gmail',
  'smtp',
  'sendgrid',
  'mailchimp',
  'postmark',
  // Messaging & chat
  'teams',
  'microsoftteams',
  'slack',
  'twiliosms',
  'twilio',
  'telegram',
  'whatsapp',
  'skype',
  // Social / broadcast
  'twitter',
  'x',
  'linkedinv2',
  'youtube',
  'reddit',
  // Notifications
  'pushover',
  'pushbullet',
];

const BUSINESS_CONNECTORS = [
  // Microsoft 365 collaboration
  'sharepointonline',
  'onedriveforbusiness',
  'onedrive',
  'microsoftforms',
  'planner',
  'approvals',
  'todo',
  'onenote',
  'outlookcalendar',
  'office365groups',
  'microsoftproject',
  'bookings',
  'excelonline',
  'excelonlinebusiness',
  // CRM & sales
  'dynamicscrm',
  'dynamics365',
  'dynamicscrmce',
  'salesforce',
  'hubspot',
  'zendesk',
  'freshdesk',
  'servicenow',
  // ERP & finance
  'sap',
  'dynamics365finops',
  'quickbooks',
  'xero',
  // HR
  'workday',
  'bamboohr',
  // Project management
  'asana',
  'trello',
  'jira',
  'mondaycom',
  'clickup',
];

const DATA_AND_CLOUD_CONNECTORS = [
  // Databases
  'sql',
  'mysql',
  'azuremysql',
  'postgresql',
  'oracle',
  'cosmosdb',
  'mongodb',
  'redis',
  'db2',
  'snowflake',
  // Azure storage
  'azureblob',
  'azuretables',
  'azurequeues',
  'azurefile',
  'azuredatalake',
  // File systems & cloud storage
  'sftpwithssh',
  'ftp',
  'filesystem',
  'dropbox',
  'box',
  'googledrive',
  's3',
  // Azure messaging & events
  'servicebus',
  'eventgrid',
  'eventhubs',
  'notificationhubs',
  // Azure compute
  'azurefunctions',
  'azureautomation',
  'containerinstance',
  // Security
  'keyvault',
  // Monitoring & analytics
  'azuremonitor',
  'loganalytics',
  // IoT
  'azureiothub',
  'iotcentral',
  // Data platform
  'azuredataexplorer',
  'synapse',
  'datafactory',
  'azuresearch',
  // Developer tools
  'github',
  'azuredevops',
  'gitlab',
  'bitbucket',
  'pagerduty',
  'datadog',
  'newrelic',
  'splunk',
];

const BUILTIN_ACTION_CONNECTORS = [
  // Workflow primitives
  'connectionProviders/control',
  'connectionProviders/variable',
  'connectionProviders/dataOperationNew',
  'connectionProviders/schedule',
  'connectionProviders/http',
  'connectionProviders/httpWithSwagger',
  'connectionProviders/request',
  'connectionProviders/response',
  'connectionProviders/httpWebhook',
  'connectionProviders/inlineCode',
  'connectionProviders/xmlOperations',
  'connectionProviders/flatfile',
  'connectionProviders/liquidOperations',
  'connectionProviders/localWorkflowOperation',
  // Service providers (runtime, no managed connection)
  '/serviceProviders/serviceBus',
  '/serviceProviders/eventHub',
  '/serviceProviders/eventGrid',
  '/serviceProviders/azureblob',
  '/serviceProviders/azurefile',
  '/serviceProviders/azuretable',
  '/serviceProviders/azurequeues',
  '/serviceProviders/sql',
  '/serviceProviders/cosmosdb',
  '/serviceProviders/ftp',
  '/serviceProviders/sftp',
  '/serviceProviders/filesystem',
  '/serviceProviders/azureaisearch',
  '/serviceProviders/smtp',
  '/serviceProviders/keyvault',
  '/serviceProviders/appservice',
  '/serviceProviders/function',
];

const AI_ACTION_CONNECTORS = [
  'connectionProviders/agent',
  '/connectionProviders/agent',
  'azureopenai',
  'openai',
  'cognitiveservices',
  '/serviceProviders/acasession',
  'connectionProviders/inlineCode',
];

/**
 * Get trigger categories for browsing.
 * Two immediate shortcuts up top, followed by a short list of curated browse buckets.
 */
export const getTriggerCategories = (): BrowseCategory[] => [
  // Immediate triggers — add directly without browsing
  {
    key: 'manual',
    title: 'When an HTTP request is received',
    description: 'Start with an HTTP webhook or request trigger',
    icon: 'Globe',
    type: 'immediate',
    operationId: 'manual',
  },
  {
    key: 'schedule',
    title: 'On a schedule',
    description: 'Run on a recurring interval or custom schedule',
    icon: 'Clock',
    type: 'immediate',
    operationId: 'recurrence',
  },

  // Dynamic browse buckets
  {
    key: 'favorites',
    title: 'Favorites',
    description: 'Connectors you have starred for quick access',
    icon: 'Heart',
    type: 'favorites',
  },

  // Curated browse buckets
  {
    key: 'communication',
    title: 'Communication',
    description: 'Email, chat, SMS, and social events',
    icon: 'Mail',
    type: 'browse',
    connectorFilters: { connectorIds: COMMUNICATION_CONNECTORS },
  },
  {
    key: 'business',
    title: 'Business apps',
    description: 'Microsoft 365, CRM, ERP, project management, and collaboration',
    icon: 'Briefcase',
    type: 'browse',
    connectorFilters: { connectorIds: BUSINESS_CONNECTORS },
  },
  {
    key: 'data-and-cloud',
    title: 'Data, cloud & developer',
    description: 'Databases, storage, Azure services, IoT, monitoring, GitHub, and DevOps',
    icon: 'Cloud',
    type: 'browse',
    connectorFilters: { connectorIds: DATA_AND_CLOUD_CONNECTORS },
  },
  {
    key: 'all',
    title: 'All triggers',
    description: 'Browse every available trigger source',
    icon: 'Apps',
    type: 'browse',
  },
];

/**
 * Get action categories for browsing.
 * A short list of curated buckets covering built-in operations and the major
 * managed-connector categories.
 */
export const getActionCategories = (): BrowseCategory[] => [
  {
    key: 'favorites',
    title: 'Favorites',
    description: 'Connectors you have starred for quick access',
    icon: 'Heart',
    type: 'favorites',
  },
  {
    key: 'ai',
    title: 'AI & Agents',
    description: 'AI agents, Azure OpenAI, and intelligent automation',
    icon: 'Bot',
    type: 'browse',
    connectorFilters: { connectorIds: AI_ACTION_CONNECTORS },
  },
  {
    key: 'builtin',
    title: 'Built-in',
    description: 'Control flow, data ops, HTTP, schedule, and runtime service providers',
    icon: 'Box',
    type: 'browse',
    connectorFilters: { connectorIds: BUILTIN_ACTION_CONNECTORS },
  },
  {
    key: 'communication',
    title: 'Communication',
    description: 'Email, chat, SMS, and social platforms',
    icon: 'Mail',
    type: 'browse',
    connectorFilters: { connectorIds: COMMUNICATION_CONNECTORS },
  },
  {
    key: 'business',
    title: 'Business apps',
    description: 'Microsoft 365, CRM, ERP, project management, and collaboration',
    icon: 'Briefcase',
    type: 'browse',
    connectorFilters: { connectorIds: BUSINESS_CONNECTORS },
  },
  {
    key: 'data-and-cloud',
    title: 'Data, cloud & developer',
    description: 'Databases, storage, Azure services, IoT, monitoring, GitHub, and DevOps',
    icon: 'Cloud',
    type: 'browse',
    connectorFilters: { connectorIds: DATA_AND_CLOUD_CONNECTORS },
  },
  {
    key: 'all',
    title: 'All connectors',
    description: 'Browse every available action and connector',
    icon: 'Apps',
    type: 'browse',
  },
];

/**
 * Get tool categories for the agent-tool picker. Wraps the action categories
 * with two agent-only buckets: MCP servers (model-context-protocol tools the
 * agent can call) and Sub-workflows (other workflows in this Logic App, used
 * for hand-off and approval patterns). Rendered with dedicated branches in
 * `OperationPickerPanel` — they do NOT use the connector-grouped list.
 */
export const getAgentToolCategories = (): BrowseCategory[] => {
  const actionCategories = getActionCategories();
  const mcpCategory: BrowseCategory = {
    key: 'mcp',
    title: 'MCP servers',
    description: 'Connect to Model Context Protocol servers and their tools',
    icon: 'Server',
    type: 'mcp',
  };
  const subworkflowCategory: BrowseCategory = {
    key: 'subworkflows',
    title: 'Sub-workflows',
    description: 'Call another workflow in this Logic App as a tool',
    icon: 'PersonChat',
    type: 'subworkflows',
  };
  // Slot the agent-only categories right after Favorites so they sit near
  // the top of the grid (above the connector buckets).
  const favoritesIdx = actionCategories.findIndex((c) => c.type === 'favorites');
  const insertAt = favoritesIdx >= 0 ? favoritesIdx + 1 : 0;
  return [
    ...actionCategories.slice(0, insertAt),
    mcpCategory,
    subworkflowCategory,
    ...actionCategories.slice(insertAt),
  ];
};

/**
 * Connectors known to have triggers but incorrectly report capabilities from Azure API.
 * These connectors report only ["actions"] in their capabilities, but actually support triggers.
 * Used to override capability filtering in the operation picker.
 *
 * IMPORTANT: Only add connectors here that are VERIFIED to have triggers.
 * Use exact connector names (case-insensitive exact match).
 */
export const CONNECTORS_WITH_TRIGGERS_OVERRIDE: string[] = [
  // Email - verified to have "When a new email arrives" triggers
  'office365',
  'outlook',
  // Microsoft 365 - verified to have triggers
  'sharepointonline',    // When an item is created, When a file is created/modified

  // Azure Storage - verified to have triggers
  'azureblob',           // When a blob is added or modified (properties only)
  'azurequeues',         // When there are messages in a queue

  // Azure Services - verified to have triggers
  'servicebus',          // When a message is received in a queue (peek-lock/auto-complete)

  // RSS - verified to have triggers
  'rss',                 // When a feed item is published
];

/**
 * Connectors verified to expose ONLY triggers. Hidden when the user is
 * browsing actions so they never resolve to an empty operations list.
 * Match is case-insensitive exact on `connector.name`.
 *
 * IMPORTANT: Only add VERIFIED entries.
 */
export const KNOWN_TRIGGER_ONLY_CONNECTORS: string[] = [
  'youtube',
  'rss',
];

/**
 * Connectors verified to expose ONLY actions. Hidden when the user is
 * browsing triggers. Same matching rules as `KNOWN_TRIGGER_ONLY_CONNECTORS`.
 */
export const KNOWN_ACTION_ONLY_CONNECTORS: string[] = [
  'azuredatalake',
  'db2',
  'mysql',
  'oracle',
  'postgresql',
];

type OperationCapability = 'triggers' | 'actions';

/**
 * Returns true if `connector` should appear in the picker for the requested
 * operation type. Resolution order:
 *
 *   1. Known wrong-type curation (`KNOWN_*_ONLY_CONNECTORS`) — exclude.
 *   2. `CONNECTORS_WITH_TRIGGERS_OVERRIDE` — include unconditionally
 *      (workaround for connectors whose Azure capabilities lie).
 *   3. Trust declared operation-type capabilities when present.
 *   4. No declared operation-type capabilities — include (unknown shape).
 */
export const connectorSupportsOperationType = (
  connector: Connector,
  type: OperationCapability,
): boolean => {
  const name = connector.name.toLowerCase();

  const wrongTypeList = type === 'triggers'
    ? KNOWN_ACTION_ONLY_CONNECTORS
    : KNOWN_TRIGGER_ONLY_CONNECTORS;
  if (wrongTypeList.some((id) => id.toLowerCase() === name)) return false;

  const isOverridden = CONNECTORS_WITH_TRIGGERS_OVERRIDE.some(
    (override) => name.includes(override.toLowerCase()),
  );
  if (isOverridden) return true;

  const declared = (connector.properties?.capabilities ?? []).filter(
    (c): c is OperationCapability => c === 'triggers' || c === 'actions',
  );
  if (declared.length === 0) return true;

  return declared.includes(type);
};

const unsupportedConnectorIds = [
  'connectionproviders/flatfileoperations',
  'connectionproviders/xmloperations',
  'connectionproviders/liquidoperations',
  '/connectionproviders/azurefunctionoperation',
  '/connectionproviders/apimanagementoperation',
];

const filterUnsupportedConnector = (connector: Connector): boolean => {
  // Filter out connectors that are not currently supported.
  return !connector.properties.capabilities?.includes('azureConnection') && !unsupportedConnectorIds.includes(connector.id?.toLowerCase() ?? '');
};

const filterUnsupportedOperation = (operation: DiscoveryOperation): boolean => {
  // Filter out operations that are not currently supported or should not be user-facing.
  const apiId = operation.properties.api?.id?.toLowerCase() ?? '';
  return !unsupportedConnectorIds.includes(apiId);
};

const toConnector = (connector: Connector): Connector => ({
  ...connector,
  name: connector.name.split('/').slice(-1)[0] ?? connector.name, // Extract the connector name from the ID
  properties: {
    ...connector.properties,
    displayName: connector.properties.generalInformation?.displayName ?? connector.properties.displayName ?? '',
    description: connector.properties.generalInformation?.description ?? connector.properties.description ?? '',
    iconUri: connector.properties.generalInformation?.iconUrl ?? connector.properties.iconUri ?? '',
    brandColor: connector.properties.metadata?.brandColor ?? ''
  },
});


